import { createContext, useContext, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';
import { ScopedThemeProvider, useOverlayContext, useStateInRef } from 'seagull';
import { isValidHexColor } from "../../business-logic/colorsLogic";
import { ErrorText } from "../../constants";
import { useStore, useStoreActions, useStoreStatic } from "../../contexts";
import { useUploadLogo } from "../../hooks/useUploadLogo";
import { handleRequest } from "../../infra/utils";
import { selectors } from "../../store/state-selectors";
import { makeDefaultBranding } from "../../store/store.utils";
import { jsx as _jsx } from "react/jsx-runtime";
const brandingToCSSVar = {
  backgroundGradientStart: '--bg-gradient-color-1',
  backgroundGradientEnd: '--bg-gradient-color-2',
  backgroundStandard: '--bg-standard',
  backgroundLight: '--bg-light',
  buttonPrimary: '--button-primary',
  buttonSecondary: '--button-secondary',
  buttonDisabled: '--button-disabled',
  progressBarComplete: '--stepper-completed',
  progressBarIncomplete: '--stepper-incomplete',
  textStandard: '--text-standard',
  textSecondary: '--text-secondary'
};
function getBrandCSS(branding, fallbackBranding) {
  var _ret$ButtonPrimary;
  const ret = {};
  Object.entries(brandingToCSSVar).forEach(_ref => {
    let [keyOfBranding, keyOfBrandCSS] = _ref;
    ret[keyOfBrandCSS] = !fallbackBranding || isValidHexColor(branding[keyOfBranding]) ? branding[keyOfBranding] : fallbackBranding[keyOfBranding];
  });
  // Derived variables for additional UI components (badges)
  const buttonPrimary = (_ret$ButtonPrimary = ret['--button-primary']) !== null && _ret$ButtonPrimary !== void 0 ? _ret$ButtonPrimary : branding.buttonPrimary;
  ret['--badge-primary-bg'] = 'transparent';
  ret['--badge-primary-border'] = buttonPrimary;
  ret['--badge-primary-color'] = buttonPrimary;
  return ret;
}
export function OverrideBranding(_ref2) {
  let {
    children,
    className,
    testId
  } = _ref2;
  const brandCSS = useMemo(() => getBrandCSS(makeDefaultBranding()), []);
  return /*#__PURE__*/_jsx(ScopedThemeProvider, {
    className: className,
    theme: brandCSS,
    testId: testId,
    children: children
  });
}
const CobBrandContext = /*#__PURE__*/createContext({
  branding: makeDefaultBranding(),
  logoImage: '',
  isDirty: false,
  errors: new Set(),
  isValid: true,
  setBranding: () => {},
  revertToDefault: () => {},
  revertToInitial: () => {},
  saveLogoAndBranding: async () => {},
  setLogo: () => {}
});
export function CobBrandingProvider(props) {
  const getProps = useStateInRef(props);
  const {
    startLoader,
    stopLoader,
    showErrorToast,
    showSuccessToast
  } = useOverlayContext();
  const businessBranding = useStore(selectors.user.branding);
  const worksAt = useStore(selectors.user.business);
  const {
    getState
  } = useStoreStatic();
  const {
    api,
    setBranding: setBusinessBranding,
    logBI
  } = useStoreActions();
  const {
    uploadLogo
  } = useUploadLogo(worksAt);
  const getUploadLogo = useStateInRef(uploadLogo);
  const [logo, _setLogo] = useState();
  const businessLogoUrl = worksAt === null || worksAt === void 0 ? void 0 : worksAt.logoUrl;
  const initialBranding = useMemo(() => props.initialBranding || businessBranding, [businessBranding, props.initialBranding]);
  const [branding, setBranding] = useState(initialBranding);
  const value = useMemo(() => {
    const logoUrl = logo === null || logo === void 0 ? void 0 : logo.asDataURL;
    const isBrandingDirty = !isEqual(branding, initialBranding);
    const isLogoDirty = !!logoUrl && logoUrl !== businessLogoUrl;
    const isDirty = isBrandingDirty || isLogoDirty;
    const errors = new Set(Object.entries(branding).filter(_ref3 => {
      let [, value] = _ref3;
      return !isValidHexColor(value);
    }).map(_ref4 => {
      let [key] = _ref4;
      return key;
    }));
    const isValid = errors.size === 0;
    return {
      branding,
      logoImage: logoUrl || businessLogoUrl || '',
      isDirty,
      errors,
      isValid,
      setLogo: logo => {
        var _getProps$onLogoChang, _getProps;
        _setLogo(logo);
        (_getProps$onLogoChang = (_getProps = getProps()).onLogoChange) === null || _getProps$onLogoChang === void 0 || _getProps$onLogoChang.call(_getProps, logo.asDataURL);
      },
      setBranding,
      revertToDefault: () => {
        logBI(11218);
        setBranding(makeDefaultBranding());
      },
      revertToInitial: () => {
        setBranding(initialBranding);
        _setLogo(undefined);
      },
      saveLogoAndBranding: async () => {
        const businessId = selectors.user.businessId(getState());
        if (!isValid || !isDirty || !businessId) {
          return;
        }
        logBI(11219);
        startLoader();
        const [updateBranding, {
          success: uploadLogoSuccess
        }] = await Promise.all([isBrandingDirty ? handleRequest(api.user.updateBranding(businessId, branding)).then(result => {
          if (!result.success) {
            showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'update-branding-error');
          } else {
            setBusinessBranding(branding);
          }
          return result;
        }) : handleRequest(Promise.resolve()), isLogoDirty ? getUploadLogo()(logo) : Promise.resolve({
          success: true
        })]);
        if (uploadLogoSuccess && updateBranding.success) {
          showSuccessToast('Saved successfully', 'update-branding-success');
        }
        stopLoader();
      }
    };
  }, [branding, initialBranding, logo, businessLogoUrl]);
  const brandCSS = useMemo(() => getBrandCSS(branding, initialBranding), [branding, initialBranding]);
  return /*#__PURE__*/_jsx(CobBrandContext.Provider, {
    value: value,
    children: /*#__PURE__*/_jsx(ScopedThemeProvider, {
      maxHeight: true,
      theme: brandCSS,
      children: props.children
    })
  });
}
export const useCobBranding = () => useContext(CobBrandContext);